import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ThemaCode } from "reedy-data/api";
import { SearchBar } from "~/components/search/search-bar";
import { Switch } from "~/components/ui/switch";
import { ThemaCodesSearchControlsSelectionDisplay } from "./thema-codes-search-controls-selection-display";
import { SearchType } from "~/shared/types/thema";

interface SearchControlsProps {
  searchType: SearchType;
  onSearchTypeChange: (type: SearchType) => void;
  query: string;
  onQueryChange: (query: string) => void;
  onQuerySubmit?: (query: string) => void;
  selected?: ThemaCode[];
  onRemoveSelected?: (code: ThemaCode) => void;
  showNotesInline: boolean;
  onToggleNotesInline: () => void;
  canSelect?: boolean;
  trailing?: ReactNode;
}

export const ThemaCodesSearchControls = ({
  searchType,
  onSearchTypeChange,
  query,
  onQueryChange,
  onQuerySubmit,
  selected = [],
  onRemoveSelected,
  showNotesInline,
  onToggleNotesInline,
  canSelect,
  trailing,
}: SearchControlsProps) => {
  const { t } = useTranslation(["components"]);

  const searchTypeOptions = [
    {
      value: SearchType.QUICK,
      label: t("components:themaCodes.searchType.quickSearch"),
    },
  ];

  return (
    <div role="search" className="flex space-x-2 mb-2 justify-between h-11">
      <SearchBar
        searchType={searchType}
        searchTypeOptions={searchTypeOptions}
        onSearchTypeChange={onSearchTypeChange}
        searchQuery={query}
        onSearchQueryChange={onQueryChange}
        onSearchSubmit={onQuerySubmit ? () => onQuerySubmit(query) : undefined}
        onClear={() => {
          onQueryChange("");
          onQuerySubmit?.("");
        }}
        placeholder={t("components:themaCodes.searchPlaceholder")}
        className="min-w-125"
        showEnterHint={Boolean(onQuerySubmit)}
        showClearOnHover={Boolean(onQuerySubmit)}
      />
      {canSelect && onRemoveSelected ? (
        <ThemaCodesSearchControlsSelectionDisplay
          selected={selected}
          onRemove={onRemoveSelected}
        />
      ) : null}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={showNotesInline}
            onCheckedChange={onToggleNotesInline}
            id="scope-notes-toggle"
          />
          <label
            htmlFor="scope-notes-toggle"
            className="text-sm font-medium text-secondary-700 cursor-pointer"
          >
            {t("components:themaCodes.scopeNotes")}
          </label>
        </div>
        {trailing}
      </div>
    </div>
  );
};
